import React, { memo, useEffect, useState } from 'react';
import useStyles from 'isomorphic-style-loader/useStyles';

import highlightJS from 'highlight.js/lib/core';
import python from 'highlight.js/lib/languages/python';
import ts from 'highlight.js/lib/languages/typescript';
import html from 'highlight.js/lib/languages/xml';
import css from 'highlight.js/lib/languages/css';
import javascript from 'highlight.js/lib/languages/javascript';
import node from 'highlight.js/lib/languages/node-repl';
import java from 'highlight.js/lib/languages/java';
import kotlin from 'highlight.js/lib/languages/kotlin';
import cpp from 'highlight.js/lib/languages/cpp';
import csharp from 'highlight.js/lib/languages/csharp';
import c from 'highlight.js/lib/languages/c';
import go from 'highlight.js/lib/languages/go';
import r from 'highlight.js/lib/languages/r';
import sql from 'highlight.js/lib/languages/sql';
import ruby from 'highlight.js/lib/languages/ruby';
import swift from 'highlight.js/lib/languages/swift';
import php from 'highlight.js/lib/languages/php';

import s from './SlCodeSnippet.scss';
import { CodeSnippetLanguage, UIComponentType } from '../../../../api/private/learn-engine-api/learn-engine-api.interface';
import { i18n } from '../../../../symphony';

interface ISlCodeSnippet {
	content: string;
	language?: CodeSnippetLanguage;
	onClick?: (a: UIComponentType) => void;
	hideCopyButton?: boolean;
}

const languages = {
	[CodeSnippetLanguage.Python]: 'PY',
	[CodeSnippetLanguage.HTML]: 'HTML',
	[CodeSnippetLanguage.CSS]: 'CSS',
	[CodeSnippetLanguage.JS]: 'JS',
	[CodeSnippetLanguage.NodeJS]: 'NODE',
	[CodeSnippetLanguage.Java]: 'JAVA',
	[CodeSnippetLanguage.Kotlin]: 'KT',
	[CodeSnippetLanguage.CPP]: 'C++',
	[CodeSnippetLanguage.CSHARP]: 'C#',
	[CodeSnippetLanguage.C]: 'C',
	[CodeSnippetLanguage.Go]: 'GO',
	[CodeSnippetLanguage.R]: 'R',
	[CodeSnippetLanguage.SQL]: 'SQL',
	[CodeSnippetLanguage.Ruby]: 'RB',
	[CodeSnippetLanguage.Swift]: 'SWIFT',
	[CodeSnippetLanguage.PHP]: 'PHP',
	[CodeSnippetLanguage.TS]: 'TS',
	// [CodeSnippetLanguage.WEB]: 'WEB',
};

export const SlCodeSnippet = memo(
	({
		content,
		language,
		hideCopyButton = false,
		onClick,
	}: ISlCodeSnippet): JSX.Element => {
		useStyles(s);
		const [copyText, setCopyText] = useState<string>(i18n.t('learn.code-snippet.copy'));
		let timer;

		useEffect(() => {
			const listOfLanguages = {
				python,
				html,
				css,
				javascript,
				node,
				java,
				kotlin,
				cpp,
				csharp,
				c,
				go,
				r,
				sql,
				ruby,
				swift,
				php,
				ts,
			};
			Object.keys(listOfLanguages).forEach((lng) => {
				highlightJS.registerLanguage(lng, listOfLanguages[lng]);
			});
			highlightJS.highlightAll();

			return () => {
				clearTimeout(timer);
			};
		}, []);

		const onCopy = () => {
			navigator.clipboard.writeText(content);
			setCopyText(i18n.t('learn.code-snippet.copied'));
			timer = setTimeout(() => {
				setCopyText(i18n.t('learn.code-snippet.copy'));
			}, 2000);
		};

		const onClickHandler = () => {
			if (onClick) {
				onClick(UIComponentType.CodeSnippet);
			}
		};

		return (
			<pre
				sl-test-data="slCodeSnippet"
				className={`${language} sl-code-snippet`}
				onClick={onClickHandler}
			>
				<code className="sl-code-snippet__code">{content}</code>
				<div className="sl-code-snippet__right-side">
					{languages[language] && (
						<span className="sl-code-snippet__language-type">
							{languages[language]}
						</span>
					)}
					{!hideCopyButton && (
						<button
							type="button"
							onClick={onCopy}
							className="sl-code-snippet__copy-clipboard"
							sl-test-data="btnCopy"
						>
							{copyText}
						</button>
					)}
				</div>
			</pre>
		);
	},
);
